import { useEffect, useMemo } from 'react';
import { HelpToggletip, TipPlacement, Tooltip } from 'ui/tooltip';
import { StyledWarningIcon } from 'modules/webPush/WebPushV2/components/styles';
import { colors } from 'ui/theme';
import { useRestMutation } from 'utils/hooks/api';
import { StyledAudience, StyledRecipient, StyledUserListWrapper } from './UserListCount.styles';
import { isValid } from 'modules/workflows/utils/nodeUtils/fieldsMatchUtils/validation';
import { Spinner } from 'ui/spinner';
import { ApiMethod } from 'services/api';
import { useStartNodeContext } from '../StartNodeEditor.context';

const calculateListCountUrl = '/workflows/calculateScheduledUserListCount';

export const USER_LIST_COUNT_SPINNER_TEST_ID = 'user-list-count-spinner-test-id';
export const STYLED_USER_LIST_WRAPPER_TEST_ID = 'styled-user-list-wrapper-test-id';
export const WARNING_TOOLTIP_TEST_ID = 'warning-tooltip-test-id';
export const STYLED_RECIPIENT_TEST_ID = 'styled-recipient-test-id';

export const UserListCount = () => {
  const { nodeData } = useStartNodeContext();
  const { mutate, data, isLoading, error } = useRestMutation(calculateListCountUrl, ApiMethod.post);

  useEffect(() => {
    let sc;
    if (
      nodeData.searchCombo &&
      isValid({
        searchQuery: nodeData.searchCombo,
        nodeClass: '',
        nodeSuperClass: '',
      })
    ) {
      sc = nodeData.searchCombo;
    }

    if (nodeData.emailListId) {
      mutate({
        emailListId: nodeData.emailListId,
        suppressionListsIds: nodeData.suppressionListIds,
        searchCombo: sc,
      });
    }
  }, [nodeData.emailListId, nodeData.suppressionListIds, nodeData.searchCombo]);

  const shouldShowWarning = useMemo(() => {
    return data?.count >= 5 * 1000000;
  }, [data]);

  return (
    <div>
      <StyledUserListWrapper data-test={STYLED_USER_LIST_WRAPPER_TEST_ID}>
        <StyledAudience>Audience</StyledAudience>
        <StyledRecipient data-test={STYLED_RECIPIENT_TEST_ID}>
          {isLoading && <LoadingSpinner />}
          {shouldShowWarning && <Warning />}
          {data && data.count !== undefined
            ? `${(data?.count as number)?.toLocaleString()} recipients (est)`
            : null}
          <HelperTooltip />
        </StyledRecipient>
      </StyledUserListWrapper>
    </div>
  );
};

const LoadingSpinner = () => {
  return <Spinner data-test={USER_LIST_COUNT_SPINNER_TEST_ID} />;
};

const Warning = () => {
  const audienceExceeds5MWarningMessage =
    'Recipient list exceeds the maximum recommended size (5 million users). Adding more than 5 million users to a journey at once may cause performance issues.';
  return (
    <Tooltip tooltipContent={audienceExceeds5MWarningMessage}>
      <StyledWarningIcon data-test={WARNING_TOOLTIP_TEST_ID} />
    </Tooltip>
  );
};

const HelperTooltip = () => {
  const helperText =
    'Total number of users who will be added to the journey when the Schedule trigger runs. (Calculated from lists selected under Add to journey minus lists selected under Exclude from journey).';

  return (
    <HelpToggletip
      placement={TipPlacement.Left}
      tooltipContent={helperText}
      iconProps={{
        color: colors.secondaryText,
        width: '16px',
        height: '16px',
      }}
    />
  );
};
